import { importWithRetry } from "@/utils/lazy-with-retry";

import { type ActiveSubscriptionModalProps } from "../components/modals/active-subscription-modal";
import { type BookmarksOnboardingModalProps } from "../components/modals/bookmarks/bookmarks-onboarding-modal";
import { type IBoostConfirmModal } from "../components/modals/boost-confirm-modal";
import { type ComposeModalProps } from "../components/modals/compose-modal";
import { type ConfirmationModalProps } from "../components/modals/confirmation-modal";
import { type CreateGroupModalProps } from "../components/modals/create-group-modal";
import { type DownloadYourInformationModalProps } from "../components/modals/download-your-info-modal";
import { type DraftsModalProps } from "../components/modals/drafts-modal/drafts-modal";
import { type EditAttachmentsModalProps } from "../components/modals/edit-attachments-modal";
import { type EditStatusOnboardingModalProps } from "../components/modals/edit-statuses-onboarding-modal";
import { type EmbedModalProps } from "../components/modals/embed-modal";
import { type FavouritesModalProps } from "../components/modals/favourites-modal";
import { type FollowModalProps } from "../components/modals/follow-modal";
import { type GroupMembersModalProps } from "../components/modals/group-members-modal";
import { type GroupVotesOnboardingModalProps } from "../components/modals/group-votes-onboarding-modal";
import { type ManageGroupModalProps } from "../components/modals/manage-group-modal";
import { type MediaModalProps } from "../components/modals/media-modal";
import { type MentionsModalProps } from "../components/modals/mentions-modal";
import { type MuteModalProps } from "../components/modals/mute-modal";
import { type ReactionsModalProps } from "../components/modals/reactions-modal/reactions-modal";
import { type ReblogsModalProps } from "../components/modals/reblogs-modal";
import { type RecommendedExplanationModalProps } from "../components/modals/recommended-explanation-modal";
import { type RegistrationModalProps } from "../components/modals/registration-modal/registration-modal";
import { type ReplyMentionsModalProps } from "../components/modals/reply-mentions-modal";
import { type ReportModalProps } from "../components/modals/report-modal/report-modal";
import { type ScheduleModalProps } from "../components/modals/schedule-modal";
import { type SignInModalProps } from "../components/modals/sign-in-modal";
import { type SubscriptionCancelModalProps } from "../components/modals/subscription-cancel-modal";
import { type SubscriptionModalProps } from "../components/modals/subscription-modal/subscription-modal";
import { type UnauthorizedModalProps } from "../components/modals/unauthorized-modal";
import { type UnlockFeatureModalProps } from "../components/modals/unlock-feature-modal";
import { type UserConnectionsModalProps } from "../components/modals/user-connections-modal/user-connections-modal";
import { type VerifySmsModalProps } from "../components/modals/verify-sms-modal";
import { type VideoModalProps } from "../components/modals/video-modal";

export type AsyncModalsTypes = {
  ACTIVE_SUBSCRIPTION: ActiveSubscriptionModalProps;
  AD: any;
  AD_DISCLAMER: any;
  ADD_EMAIL: any;
  BOOKMARKS_ONBOARDING: BookmarksOnboardingModalProps;
  BOOST_CONFIRM: IBoostConfirmModal;
  CHANGE_EMAIL: any;
  COMPOSE: ComposeModalProps;
  CONFIRM: ConfirmationModalProps;
  CREATE_GROUP: CreateGroupModalProps;
  DRAFTS: DraftsModalProps;
  REQUEST_DATA_EXPORT: any;
  DOWNLOAD_YOUR_INFORMATION: DownloadYourInformationModalProps;
  EDIT_ATTACHMENTS: EditAttachmentsModalProps;
  EDIT_STATUSES_ONBOARDING: EditStatusOnboardingModalProps;
  EMBED: EmbedModalProps;
  FAVOURITES: FavouritesModalProps;
  FOLLOW: FollowModalProps;
  GEM_BALANCE: any;
  GROUP_MEMBERS: GroupMembersModalProps;
  GROUP_VOTES_ONBOARDING: GroupVotesOnboardingModalProps;
  HOTKEYS: any;
  MEDIA: MediaModalProps;
  VIDEO_MEDIA: VideoModalProps;
  MENTIONS: MentionsModalProps;
  MUTE: MuteModalProps;
  MANAGE_GROUP: ManageGroupModalProps;
  ONBOARDING: any;
  POLICY: any;
  REACTIONS: ReactionsModalProps;
  REBLOGS: ReblogsModalProps;
  RECOMMENDED_EXPLANATION: RecommendedExplanationModalProps;
  REGISTRATION: RegistrationModalProps;
  REMOVE_BOOKMARKS: any;
  REPLY_MENTIONS: ReplyMentionsModalProps;
  REPORT: ReportModalProps;
  RESET_USERNAME_PASSWORD: any;
  REVERIFY_SMS: any;
  SCHEDULE: ScheduleModalProps;
  SIGNIN: SignInModalProps;
  SUBSCRIPTIONS: SubscriptionModalProps;
  SUBSCRIPTION_CANCEL: SubscriptionCancelModalProps;
  SUBSCRIPTION_MANAGE_PAYMENTS: any;
  SUBSCRIPTION_REACTIVATE: any;
  TRUTH_PLUS_SUBSCRIPTIONS: any;
  UNAUTHORIZED: UnauthorizedModalProps;
  UNLOCK_FEATURE: UnlockFeatureModalProps;
  USER_CONNECTIONS: UserConnectionsModalProps;
  VERIFY_SMS: VerifySmsModalProps;
  VERIFY_DEBIT_CARD: any;
};

export const asyncModals = {
  ACTIVE_SUBSCRIPTION: () =>
    importWithRetry(
      () => import("../components/modals/active-subscription-modal"),
    ),
  AD: () => importWithRetry(() => import("../components/modals/ad-modal")),
  ADD_EMAIL: () =>
    importWithRetry(
      () => import("../components/modals/add-email-modal/add-email-modal"),
    ),
  AD_DISCLAMER: () =>
    importWithRetry(() => import("../components/modals/ad-disclamer-modal")),
  BOOKMARKS_ONBOARDING: () =>
    importWithRetry(
      () => import("../components/modals/bookmarks/bookmarks-onboarding-modal"),
    ),
  BOOST_CONFIRM: () =>
    importWithRetry(() => import("../components/modals/boost-confirm-modal")),
  CHANGE_EMAIL: () =>
    importWithRetry(
      () =>
        import("../components/modals/change-email-modal/change-email-modal"),
    ),
  COMPOSE: () =>
    importWithRetry(() => import("../components/modals/compose-modal")),
  CONFIRM: () =>
    importWithRetry(() => import("../components/modals/confirmation-modal")),
  CREATE_GROUP: () =>
    importWithRetry(() => import("../components/modals/create-group-modal")),
  DRAFTS: () =>
    importWithRetry(
      () => import("../components/modals/drafts-modal/drafts-modal"),
    ),
  REQUEST_DATA_EXPORT: () =>
    importWithRetry(
      () => import("../components/modals/request-data-export-modal"),
    ),
  DOWNLOAD_YOUR_INFORMATION: () =>
    importWithRetry(
      () => import("../components/modals/download-your-info-modal"),
    ),
  EDIT_ATTACHMENTS: () =>
    importWithRetry(
      () => import("../components/modals/edit-attachments-modal"),
    ),
  EDIT_STATUSES_ONBOARDING: () =>
    importWithRetry(
      () => import("../components/modals/edit-statuses-onboarding-modal"),
    ),
  EMBED: () =>
    importWithRetry(() => import("../components/modals/embed-modal")),
  FAVOURITES: () =>
    importWithRetry(() => import("../components/modals/favourites-modal")),
  FOLLOW: () =>
    importWithRetry(() => import("../components/modals/follow-modal")),
  GEM_BALANCE: () =>
    importWithRetry(() => import("../components/modals/gem-balance-modal")),
  GROUP_MEMBERS: () =>
    importWithRetry(() => import("../components/modals/group-members-modal")),
  GROUP_VOTES_ONBOARDING: () =>
    importWithRetry(
      () => import("../components/modals/group-votes-onboarding-modal"),
    ),
  HOTKEYS: () =>
    importWithRetry(() => import("../components/modals/hotkeys-modal")),
  MEDIA: () =>
    importWithRetry(() => import("../components/modals/media-modal")),
  VIDEO_MEDIA: () =>
    importWithRetry(() => import("../components/modals/video-modal")),
  MENTIONS: () =>
    importWithRetry(() => import("../components/modals/mentions-modal")),
  MUTE: () => importWithRetry(() => import("../components/modals/mute-modal")),
  MANAGE_GROUP: () =>
    importWithRetry(() => import("../components/modals/manage-group-modal")),
  ONBOARDING: () =>
    importWithRetry(
      () => import("../components/modals/onboarding-modal/onboarding-modal"),
    ),
  POLICY: () =>
    importWithRetry(() => import("../components/modals/policy-modal")),
  REACTIONS: () =>
    importWithRetry(
      () => import("../components/modals/reactions-modal/reactions-modal"),
    ),
  REBLOGS: () =>
    importWithRetry(() => import("../components/modals/reblogs-modal")),
  RECOMMENDED_EXPLANATION: () =>
    importWithRetry(
      () => import("../components/modals/recommended-explanation-modal"),
    ),
  REGISTRATION: () =>
    importWithRetry(
      () =>
        import("../components/modals/registration-modal/registration-modal"),
    ),
  REMOVE_BOOKMARKS: () =>
    importWithRetry(
      () => import("../components/modals/bookmarks/remove-bookmarks-modal"),
    ),
  REPLY_MENTIONS: () =>
    importWithRetry(() => import("../components/modals/reply-mentions-modal")),
  REPORT: () =>
    importWithRetry(
      () => import("../components/modals/report-modal/report-modal"),
    ),
  RESET_USERNAME_PASSWORD: () =>
    importWithRetry(
      () =>
        import("../components/modals/reset-username-password-modal/reset-username-password-modal"),
    ),
  REVERIFY_SMS: () =>
    importWithRetry(() => import("../components/modals/reverify-sms-modal")),
  SCHEDULE: () =>
    importWithRetry(() => import("../components/modals/schedule-modal")),
  SIGNIN: () =>
    importWithRetry(() => import("../components/modals/sign-in-modal")),
  SUBSCRIPTIONS: () =>
    importWithRetry(
      () =>
        import("../components/modals/subscription-modal/subscription-modal"),
    ),
  SUBSCRIPTION_CANCEL: () =>
    importWithRetry(
      () => import("../components/modals/subscription-cancel-modal"),
    ),
  SUBSCRIPTION_MANAGE_PAYMENTS: () =>
    importWithRetry(
      () => import("../components/modals/subscription-manage-payments-modal"),
    ),
  SUBSCRIPTION_REACTIVATE: () =>
    importWithRetry(
      () => import("../components/modals/subscription-reactivate-modal"),
    ),
  TRUTH_PLUS_SUBSCRIPTIONS: () =>
    importWithRetry(
      () => import("../components/modals/truth-plus-subscriptions-modal"),
    ),
  UNAUTHORIZED: () =>
    importWithRetry(() => import("../components/modals/unauthorized-modal")),
  UNLOCK_FEATURE: () =>
    importWithRetry(() => import("../components/modals/unlock-feature-modal")),
  USER_CONNECTIONS: () =>
    importWithRetry(
      () =>
        import("../components/modals/user-connections-modal/user-connections-modal"),
    ),
  VERIFY_SMS: () =>
    importWithRetry(() => import("../components/modals/verify-sms-modal")),
};
